import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Phone, TrendingUp, Users, RefreshCw, LogOut, Clock, Zap, ChevronDown, ChevronUp, FileSearch } from 'lucide-react';
import { ModalIndicacao, ModalVenda } from './ModalRegistro';

const fmt = (s) => { if (!s && s !== 0) return '00:00:00'; const n = parseInt(s); const h = Math.floor(n/3600).toString().padStart(2,'0'); const m = Math.floor((n%3600)/60).toString().padStart(2,'0'); const ss = (n%60).toString().padStart(2,'0'); return `${h}:${m}:${ss}`; };
const fmtData = (d) => { if (!d) return ''; const dt = new Date(d); return `${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`; };
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;

const COR = {
  laranja:    '#F07020',
  laranjaEsc: '#C85A10',
  azul:       '#1E6DB5',
  azulEsc:    '#144E8A',
  ciano:      '#29B6D8',
  cianoEsc:   '#1A8FAD',
  verde:      '#22C55E',
  verdeEsc:   '#16A34A',
  amarelo:    '#F5A623',
  vermelho:   '#EF4444',
  bg:         '#0A0F1A',
  card:       '#0E1826',
  borda:      '#1A2740',
  texto:      '#F1F5F9',
  subTexto:   '#64748B',
  subTexto2:  '#334155',
};

const METAS_URA  = { qualificadas: 50,   indicacoes: 60,   vendas: 45 };
const METAS_DISC = { qualificadas: 14.5, indicacoes: 23.5, vendas: 34 };
const ORDINAL    = ['', '1ª', '2ª', '3ª', '4ª', '5ª'];

export default function Dashboard() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [dupContagem, setDupContagem] = React.useState({ pendentes: 0, novidades: 0 });
  const [periodo, setPeriodo] = useState('hoje');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [hora, setHora] = useState(new Date().toLocaleTimeString('pt-BR'));
  const [modalIndicacao, setModalIndicacao] = useState(false);
  const [modalVenda, setModalVenda] = useState(false);
  const [semanaExpandida, setSemanaExpandida] = useState(null);

  const carregar = useCallback(async (manual = false) => {
    if (manual) setAtualizando(true); else setLoading(true);
    try {
      const ehSupervisor = usuario?.perfil === 'supervisor' || usuario?.perfil === 'administrador';
      if (ehSupervisor) {
        const res = await api.get(`/metricas/equipe?periodo=${periodo}`);
        const ops = res.data.operadores || [];
        const totais = res.data.totais || {};
        setDados({
          ligacoes: {
            [periodo]: {
              total: totais.ligacoes || 0,
              qualificadas: totais.qualificadas || 0,
              ura: ops.reduce((a, o) => a + o.ligacoes.ura, 0),
              discador: ops.reduce((a, o) => a + o.ligacoes.discador, 0),
              qual_ura: ops.reduce((a, o) => a + o.ligacoes.qual_ura, 0),
              qual_discador: ops.reduce((a, o) => a + o.ligacoes.qual_discador, 0),
              tempoTotal: totais.tempo_total || 0,
              tempoMedio: 0,
            }
          },
          vendas: {
            [periodo]: {
              total: totais.vendas || 0,
              ura: ops.reduce((a, o) => a + o.vendas.ura, 0),
              discador: ops.reduce((a, o) => a + o.vendas.discador, 0),
            }
          },
          indicacoes: {
            [periodo]: {
              total: totais.indicacoes || 0,
              ura: ops.reduce((a, o) => a + o.indicacoes.ura, 0),
              discador: ops.reduce((a, o) => a + o.indicacoes.discador, 0),
            }
          },
          evolucaoLigacoes: [], evolucaoVendInd: [],
          detalheLigacoes: [], detalheVendas: [], detalheIndicacoes: [],
          semanasDoMes: [], diasDaSemana: [],
        });
      } else {
        const res = await api.get('/metricas/meus-dados');
        setDados(res.data);
      }
      setHora(new Date().toLocaleTimeString('pt-BR'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); setAtualizando(false); }
  }, [periodo, usuario?.perfil]);

  useEffect(() => { carregar(); const t = setInterval(() => carregar(), 5*60*1000); return () => clearInterval(t); }, [carregar]);

  React.useEffect(() => {
    if (usuario?.perfil === 'operador') {
      api.get('/registros/duplicidades/minhas/contagem')
        .then(r => setDupContagem(r.data))
        .catch(() => {});
    }
  }, [usuario]);

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) return (
    <div style={s.loadWrap}>
      <div style={s.loadSpinner}/>
      <p style={{color: COR.subTexto, marginTop:16, fontFamily:'sans-serif'}}>Carregando dados...</p>
    </div>
  );

  const d = dados;
  const p = periodo;
  const lig  = d?.ligacoes?.[p]   || {};
  const vend = d?.vendas?.[p]     || {};
  const ind  = d?.indicacoes?.[p] || {};
  const semanas = d?.semanasDoMes || [];
  const diasSemana = d?.diasDaSemana || [];
  const ehOperador = usuario?.perfil === 'operador';

  return (
    <div style={s.page}>
      <div style={s.topBar}/>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logoBox}>
            <span style={s.logoA}>A</span>
            <span style={s.logoResto}>TTIVA</span>
            <span style={s.logoBadge}>PAGAMENTOS</span>
          </div>
          <div style={s.headerDivider}/>
          <div>
            <div style={s.aovivo}><span style={s.dot}/> AO VIVO</div>
            <h1 style={s.nome}>Olá, {usuario?.nome?.split(' ')[0]} 👋</h1>
            <p style={s.sub}>Atualizado às {hora}</p>
          </div>
        </div>
        <div style={s.headerRight}>
          <div style={s.infoBox}><Clock size={14} color={COR.laranja}/> <span style={s.infoLabel}>Tempo em fala hoje</span> <span style={s.infoVal}>{fmt(d?.ligacoes?.hoje?.tempoTotal)}</span></div>
          <div style={s.infoBox}><Zap size={14} color={COR.ciano}/> <span style={s.infoLabel}>Tempo médio</span> <span style={s.infoVal}>{fmt(d?.ligacoes?.hoje?.tempoMedio)}</span></div>
          <button style={s.btnIcon} onClick={() => carregar(true)} disabled={atualizando}><RefreshCw size={14} style={atualizando?{animation:'spin 1s linear infinite'}:{}}/></button>
          {usuario?.perfil === 'administrador' && (
            <button style={s.btnGestor} onClick={() => navigate('/usuarios')}><Users size={14}/> Usuários</button>
          )}
          {(usuario?.perfil === 'supervisor' || usuario?.perfil === 'administrador') && (
            <button style={s.btnEquipe} onClick={() => navigate('/equipe')}><Users size={14}/> Minha Equipe</button>
          )}
          {usuario?.perfil === 'operador' && (
            <button
              style={{
                display:'flex',alignItems:'center',gap:6,
                background: dupContagem.pendentes > 0 || dupContagem.novidades > 0 ? '#F5A62322' : 'transparent',
                border: `1px solid ${dupContagem.pendentes > 0 ? '#F5A62366' : dupContagem.novidades > 0 ? '#22C55E66' : '#1A2740'}`,
                borderRadius:8, padding:'7px 13px',
                color: dupContagem.pendentes > 0 ? '#F5A623' : dupContagem.novidades > 0 ? '#22C55E' : '#64748B',
                cursor:'pointer', fontSize:12, fontWeight:600, position:'relative',
              }}
              onClick={() => navigate('/minhas-duplicidades')}>
              <FileSearch size={14}/>
              Duplicidades
              {(dupContagem.pendentes > 0 || dupContagem.novidades > 0) && (
                <span style={{
                  background: dupContagem.pendentes > 0 ? '#F5A623' : '#22C55E',
                  color:'#fff', borderRadius:'50%', width:16, height:16,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:10, fontWeight:800, marginLeft:2,
                }}>
                  {dupContagem.pendentes > 0
                    ? (dupContagem.pendentes > 9 ? '9+' : dupContagem.pendentes)
                    : (dupContagem.novidades > 9 ? '9+' : dupContagem.novidades)}
                </span>
              )}
            </button>
          )}
          <button style={s.btnLogout} onClick={handleLogout}><LogOut size={14}/> Sair</button>
        </div>
      </div>

      {/* FILTROS */}
      <div style={s.filtros}>
        {['hoje','semana','mes'].map(f => (
          <button key={f} style={{...s.filtro,...(periodo===f?s.filtroAtivo:{})}} onClick={() => setPeriodo(f)}>
            {f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>

      {/* LAYOUT PRINCIPAL */}
      <div style={s.mainLayout}>
        <div style={s.leftCol}>

          {/* CARDS PRINCIPAIS */}
          <div style={s.cardsRow}>
            <CardMetrica icon={<Phone size={16} color={COR.laranja}/>} titulo="Ligações" total={lig.total||0} ura={lig.ura||0} discador={lig.discador||0} cor={COR.laranja}/>
            <CardMetrica icon={<Zap size={16} color={COR.ciano}/>} titulo="Lig. Qualificadas" total={lig.qualificadas||0} ura={lig.qual_ura||0} discador={lig.qual_discador||0} cor={COR.ciano}/>
          </div>
          <div style={s.cardsRow}>
            <CardMetrica
              icon={<Users size={16} color={COR.amarelo}/>} titulo="Indicações"
              total={ind.total||0} ura={ind.ura||0} discador={ind.discador||0} cor={COR.amarelo}
              onRegistrar={ehOperador && periodo === 'hoje' ? () => setModalIndicacao(true) : null}
              labelBtn="+ Registrar Indicação"
            />
            <CardMetrica
              icon={<TrendingUp size={16} color={COR.verde}/>} titulo="Vendas"
              total={vend.total||0} ura={vend.ura||0} discador={vend.discador||0} cor={COR.verde}
              onRegistrar={ehOperador && periodo === 'hoje' ? () => setModalVenda(true) : null}
              labelBtn="+ Registrar Venda"
            />
          </div>

          {/* TABELAS — apenas hoje */}
          {periodo === 'hoje' && (
            <div style={s.tabelasRow}>
              <TabelaDetalhe titulo="Lig. Qualificadas — Hoje" cor={COR.ciano} colunas={['Horário','Empresa','Tempo','UF','Modo']}
                dados={(d?.detalheLigacoes||[]).map(r => [fmtData(r.data_hora_ligacao), r.razao_social||'—', fmt(r.tempo_falado), r.uf||'—', r.modo||'—'])}/>
              <TabelaDetalhe titulo="Indicações — Hoje" cor={COR.amarelo} colunas={['Horário','Empresa','CNPJ','Cidade','Origem']}
                dados={(d?.detalheIndicacoes||[]).map(r => [fmtData(r.carimbo_data_hora), r.razao_social||'—', r.cnpj||'—', r.cidade||'—', r.origem||'—'])}/>
              <TabelaDetalhe titulo="Vendas — Hoje" cor={COR.verde} colunas={['Horário','Empresa','CNPJ','Cidade','Origem']}
                dados={(d?.detalheVendas||[]).map(r => [fmtData(r.carimbo_data_hora), r.razao_social||'—', r.cnpj||'—', r.cidade||'—', r.origem||'—'])}/>
            </div>
          )}

          {/* BREAKDOWN DIÁRIO — semana */}
          {periodo === 'semana' && diasSemana.length > 0 && (
            <div>
              <Separador label="Resultado por Dia"/>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {diasSemana.map(dia => <CardDia key={dia.dia} dia={dia}/>)}
              </div>
            </div>
          )}

          {/* QUEBRA SEMANAL — mês com expansão por dia */}
          {periodo === 'mes' && semanas.length > 0 && (
            <div>
              <Separador label="Quebra por Semana"/>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {semanas.map(sem => (
                  <CardSemana
                    key={sem.semana}
                    semana={sem}
                    expandido={semanaExpandida === sem.semana}
                    onToggle={() => setSemanaExpandida(semanaExpandida === sem.semana ? null : sem.semana)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* GRÁFICOS */}
          <div style={s.graficosRow}>
            <div style={s.grafico}>
              <p style={s.graficoTitulo}><Phone size={14} color={COR.laranja}/> Ligações — Últimos 7 dias</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={d?.evolucaoLigacoes||[]} margin={{top:5,right:5,bottom:5,left:-20}}>
                  <XAxis dataKey="dia" tickFormatter={v => v ? v.slice(5,10).split('-').reverse().join('/') : ''} tick={{fill:COR.subTexto,fontSize:11}}/>
                  <YAxis tick={{fill:COR.subTexto,fontSize:11}}/>
                  <Tooltip contentStyle={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8}} labelFormatter={v => v ? v.slice(5,10).split('-').reverse().join('/') : ''}/>
                  <Legend wrapperStyle={{fontSize:11}}/>
                  <Bar dataKey="discador" stackId="a" fill={COR.azul} name="Discador"/>
                  <Bar dataKey="ura" stackId="a" fill={COR.laranja} name="URA" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={s.grafico}>
              <p style={s.graficoTitulo}><TrendingUp size={14} color={COR.verde}/> Vendas & Indicações — Mês</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={d?.evolucaoVendInd||[]} margin={{top:5,right:5,bottom:5,left:-20}}>
                  <XAxis dataKey="dia" tickFormatter={v => v ? v.slice(5,10).split('-').reverse().join('/') : ''} tick={{fill:COR.subTexto,fontSize:11}}/>
                  <YAxis tick={{fill:COR.subTexto,fontSize:11}}/>
                  <Tooltip contentStyle={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8}} labelFormatter={v => v ? v.slice(5,10).split('-').reverse().join('/') : ''}/>
                  <Legend wrapperStyle={{fontSize:11}}/>
                  <Line type="monotone" dataKey="indicacoes" stroke={COR.amarelo} strokeWidth={2} dot={{r:3}} name="Indicações"/>
                  <Line type="monotone" dataKey="vendas" stroke={COR.verde} strokeWidth={2} dot={{r:3}} name="Vendas"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* FUNIL */}
        <div style={s.funnelCol}>
          <div style={s.funnelHeader}>
            <p style={s.funnelTitulo}>Funil de Conversão</p>
            <p style={s.funnelSub}>{periodo.charAt(0).toUpperCase()+periodo.slice(1)}</p>
          </div>
          <FunnelVisual titulo="URA" icone="↔"
            coresEtapas={[COR.laranja, COR.ciano, COR.amarelo, COR.verde]}
            etapas={[
              { label:'Atendidas',    valor: lig.ura||0,          meta: null,                   base: null },
              { label:'Qualificadas', valor: lig.qual_ura||0,     meta: METAS_URA.qualificadas,  base: lig.ura||0 },
              { label:'Indicações',   valor: ind.ura||0,          meta: METAS_URA.indicacoes,    base: lig.qual_ura||0 },
              { label:'Vendas',       valor: vend.ura||0,         meta: METAS_URA.vendas,        base: ind.ura||0 },
            ]}
          />
          <div style={s.funnelDivider}/>
          <FunnelVisual titulo="Discador" icone="⚡"
            coresEtapas={[COR.azul, COR.ciano, COR.amarelo, COR.verde]}
            etapas={[
              { label:'Atendidas',    valor: lig.discador||0,      meta: null,                    base: null },
              { label:'Qualificadas', valor: lig.qual_discador||0, meta: METAS_DISC.qualificadas,  base: lig.discador||0 },
              { label:'Indicações',   valor: ind.discador||0,      meta: METAS_DISC.indicacoes,    base: lig.qual_discador||0 },
              { label:'Vendas',       valor: vend.discador||0,     meta: METAS_DISC.vendas,        base: ind.discador||0 },
            ]}
          />
        </div>
      </div>

      {/* MODAIS */}
      {modalIndicacao && <ModalIndicacao onClose={() => setModalIndicacao(false)} onSuccess={() => carregar(true)}/>}
      {modalVenda && <ModalVenda onClose={() => setModalVenda(false)} onSuccess={() => carregar(true)}/>}
    </div>
  );
}

// ── Separador ────────────────────────────────────────────────────────────────
function Separador({ label }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,marginTop:4}}>
      <div style={{height:1,flex:1,background:COR.borda}}/>
      <span style={{color:COR.subTexto,fontSize:11,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase'}}>{label}</span>
      <div style={{height:1,flex:1,background:COR.borda}}/>
    </div>
  );
}

// ── Card de um DIA (usado na semana e na expansão do mês) ─────────────────────
function CardDia({ dia }) {
  const d = dia.dia ? dia.dia.slice(5,10).split('-').reverse().join('/') : '';

  // Conversões URA
  const qualUra  = pct(dia.ligacoes.qual_ura,    dia.ligacoes.ura);
  const indUra   = pct(dia.indicacoes.ura,        dia.ligacoes.qual_ura);
  const vendaUra = pct(dia.vendas.ura,            dia.indicacoes.ura);

  // Conversões Discador
  const qualDisc  = pct(dia.ligacoes.qual_discador, dia.ligacoes.discador);
  const indDisc   = pct(dia.indicacoes.discador,     dia.ligacoes.qual_discador);
  const vendaDisc = pct(dia.vendas.discador,         dia.indicacoes.discador);

  return (
    <div style={{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,padding:'12px 16px'}}>
      {/* Cabeçalho do dia */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
        <span style={{color:COR.laranja,fontSize:13,fontWeight:800}}>{dia.diaSemana}</span>
        <span style={{color:COR.subTexto,fontSize:11}}>{d}</span>
        {/* Mini funil inline — dois funis URA e Discador */}
        <div style={{marginLeft:'auto',display:'flex',gap:12,fontSize:10}}>
          <div style={{display:'flex',alignItems:'center',gap:4}}>
            <span style={{color:COR.laranja,fontSize:9,fontWeight:700}}>↔</span>
            <Tag cor={COR.laranja} label={`${dia.ligacoes.ura}`}/>
            <Seta/>
            <Tag cor={COR.ciano}   label={`${qualUra}%`}/>
            <Seta/>
            <Tag cor={COR.amarelo} label={`${indUra}%`}/>
            <Seta/>
            <Tag cor={COR.verde}   label={`${vendaUra}%`}/>
          </div>
          <div style={{width:1,background:COR.borda}}/>
          <div style={{display:'flex',alignItems:'center',gap:4}}>
            <span style={{color:COR.azul,fontSize:9,fontWeight:700}}>⚡</span>
            <Tag cor={COR.azul}    label={`${dia.ligacoes.discador}`}/>
            <Seta/>
            <Tag cor={COR.ciano}   label={`${qualDisc}%`}/>
            <Seta/>
            <Tag cor={COR.amarelo} label={`${indDisc}%`}/>
            <Seta/>
            <Tag cor={COR.verde}   label={`${vendaDisc}%`}/>
          </div>
        </div>
      </div>

      {/* Métricas do dia */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
        <MiniMetricaDia titulo="Ligações" valor={dia.ligacoes.total} cor={COR.laranja}
          sub1={`↔ ${dia.ligacoes.ura}`} sub2={`⚡ ${dia.ligacoes.discador}`}/>
        <MiniMetricaDia titulo="Qualificadas" valor={dia.ligacoes.qualificadas} cor={COR.ciano}
          sub1={`↔ ${dia.ligacoes.qual_ura} (${qualUra}%)`} sub2={`⚡ ${dia.ligacoes.qual_discador} (${qualDisc}%)`}/>
        <MiniMetricaDia titulo="Indicações" valor={dia.indicacoes.total} cor={COR.amarelo}
          sub1={`↔ ${dia.indicacoes.ura} (${indUra}%)`} sub2={`⚡ ${dia.indicacoes.discador} (${indDisc}%)`}/>
        <MiniMetricaDia titulo="Vendas" valor={dia.vendas.total} cor={COR.verde}
          sub1={`↔ ${dia.vendas.ura} (${vendaUra}%)`} sub2={`⚡ ${dia.vendas.discador} (${vendaDisc}%)`}/>
      </div>
    </div>
  );
}

function Tag({ cor, label, ok }) {
  const color = ok === undefined ? cor : ok ? COR.verde : COR.vermelho;
  return (
    <span style={{background:`${color}22`,border:`1px solid ${color}44`,borderRadius:4,padding:'2px 6px',color:color,fontWeight:700,whiteSpace:'nowrap'}}>
      {label}
    </span>
  );
}

function Seta() {
  return <span style={{color:COR.subTexto,fontSize:10}}>→</span>;
}

function MiniMetricaDia({ titulo, valor, cor, sub1, sub2, pct: pctVal, meta }) {
  const ok = pctVal !== undefined && meta !== undefined ? pctVal >= meta : null;
  return (
    <div style={{background:`${cor}10`,border:`1px solid ${cor}30`,borderRadius:8,padding:'8px 10px'}}>
      <p style={{color:cor,fontSize:10,fontWeight:700,margin:'0 0 2px 0',textTransform:'uppercase',letterSpacing:0.5}}>{titulo}</p>
      <p style={{color:cor,fontSize:22,fontWeight:800,margin:'0 0 4px 0',lineHeight:1}}>{valor}</p>
      {pctVal !== undefined && (
        <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:4}}>
          <div style={{width:5,height:5,borderRadius:'50%',background:ok?COR.verde:COR.vermelho,boxShadow:`0 0 6px ${ok?COR.verde:COR.vermelho}`}}/>
          <span style={{color:ok?COR.verde:COR.vermelho,fontSize:10,fontWeight:700}}>{pctVal}%</span>
          <span style={{color:COR.subTexto,fontSize:9}}>meta {meta}%</span>
        </div>
      )}
      <div style={{display:'flex',justifyContent:'space-between'}}>
        <span style={{color:COR.subTexto,fontSize:9}}>{sub1}</span>
        <span style={{color:COR.subTexto,fontSize:9}}>{sub2}</span>
      </div>
    </div>
  );
}

// ── Card Semana com expansão de dias ─────────────────────────────────────────
function CardSemana({ semana, expandido, onToggle }) {
  const num = semana.semana;
  const label = (ORDINAL[num] || `${num}ª`) + ' Semana';
  const diasInicio = (num - 1) * 7 + 1;
  const diasFim = Math.min(num * 7, 31);

  const qualUra   = pct(semana.ligacoes.qual_ura,    semana.ligacoes.ura);
  const indUra    = pct(semana.indicacoes.ura,        semana.ligacoes.qual_ura);
  const vendaUra  = pct(semana.vendas.ura,            semana.indicacoes.ura);
  const qualDisc  = pct(semana.ligacoes.qual_discador, semana.ligacoes.discador);
  const indDisc   = pct(semana.indicacoes.discador,    semana.ligacoes.qual_discador);
  const vendaDisc = pct(semana.vendas.discador,        semana.indicacoes.discador);

  return (
    <div style={{background:COR.card,border:`1px solid ${expandido?COR.laranja+'55':COR.borda}`,borderRadius:12,overflow:'hidden'}}>
      <div style={{padding:'14px 18px',cursor:'pointer'}} onClick={onToggle}>
        <div style={{display:'flex',alignItems:'center',marginBottom:10}}>
          <span style={{color:COR.texto,fontSize:13,fontWeight:700}}>{label}</span>
          <span style={{color:COR.subTexto,fontSize:11,marginLeft:8}}>Dias {diasInicio}–{diasFim}</span>
          <div style={{marginLeft:'auto',display:'flex',gap:10,fontSize:10,marginRight:10}}>
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{color:COR.laranja,fontSize:9,fontWeight:700}}>↔</span>
              <Tag cor={COR.laranja} label={`${semana.ligacoes.ura}`}/>
              <Seta/><Tag cor={COR.ciano}   label={`${qualUra}%`}/>
              <Seta/><Tag cor={COR.amarelo} label={`${indUra}%`}/>
              <Seta/><Tag cor={COR.verde}   label={`${vendaUra}%`}/>
            </div>
            <div style={{width:1,background:COR.borda}}/>
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{color:COR.azul,fontSize:9,fontWeight:700}}>⚡</span>
              <Tag cor={COR.azul}    label={`${semana.ligacoes.discador}`}/>
              <Seta/><Tag cor={COR.ciano}   label={`${qualDisc}%`}/>
              <Seta/><Tag cor={COR.amarelo} label={`${indDisc}%`}/>
              <Seta/><Tag cor={COR.verde}   label={`${vendaDisc}%`}/>
            </div>
          </div>
          <span style={{color:COR.subTexto}}>{expandido ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}</span>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          <MiniMetrica titulo="Ligações"    valor={semana.ligacoes.total}    cor={COR.laranja} sub1={`↔ ${semana.ligacoes.ura}`}    sub2={`⚡ ${semana.ligacoes.discador}`}/>
          <MiniMetrica titulo="Qualificadas"valor={semana.ligacoes.qualificadas}cor={COR.ciano}sub1={`↔ ${semana.ligacoes.qual_ura} (${qualUra}%)`}sub2={`⚡ ${semana.ligacoes.qual_discador} (${qualDisc}%)`}/>
          <MiniMetrica titulo="Indicações"  valor={semana.indicacoes.total}  cor={COR.amarelo} sub1={`↔ ${semana.indicacoes.ura} (${indUra}%)`} sub2={`⚡ ${semana.indicacoes.discador} (${indDisc}%)`}/>
          <MiniMetrica titulo="Vendas"      valor={semana.vendas.total}      cor={COR.verde}   sub1={`↔ ${semana.vendas.ura} (${vendaUra}%)`}    sub2={`⚡ ${semana.vendas.discador} (${vendaDisc}%)`}/>
        </div>
      </div>

      {expandido && semana.dias && semana.dias.length > 0 && (
        <div style={{borderTop:`1px solid ${COR.borda}`,padding:'12px 18px',background:'#080f1a',display:'flex',flexDirection:'column',gap:8}}>
          <p style={{color:COR.subTexto,fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:1,margin:'0 0 4px 0'}}>Detalhamento por dia</p>
          {semana.dias.map(dia => <CardDia key={dia.dia} dia={dia}/>)}
        </div>
      )}
    </div>
  );
}

function MiniMetrica({ titulo, valor, cor, sub1, sub2 }) {
  return (
    <div style={{background:`${cor}10`,border:`1px solid ${cor}30`,borderRadius:8,padding:'10px 12px'}}>
      <p style={{color:cor,fontSize:10,fontWeight:700,margin:'0 0 4px 0',textTransform:'uppercase',letterSpacing:0.5}}>{titulo}</p>
      <p style={{color:cor,fontSize:26,fontWeight:800,margin:'0 0 6px 0',lineHeight:1}}>{valor}</p>
      <div style={{display:'flex',justifyContent:'space-between'}}>
        <span style={{color:COR.subTexto,fontSize:10}}>{sub1}</span>
        <span style={{color:COR.subTexto,fontSize:10}}>{sub2}</span>
      </div>
    </div>
  );
}

function CardMetrica({ icon, titulo, total, ura, discador, cor, onRegistrar, labelBtn }) {
  return (
    <div style={{background:`linear-gradient(135deg,${cor}18,${cor}06)`,border:`1px solid ${cor}40`,borderRadius:14,padding:'14px 18px',position:'relative',overflow:'hidden'}}>
      <div style={{position:'absolute',top:-18,right:-18,width:70,height:70,borderRadius:'50%',background:`${cor}12`}}/>
      <div style={s.cardHeader}>
        {icon}
        <span style={{...s.cardTitulo,color:cor}}>{titulo}</span>
        {onRegistrar && (
          <button onClick={onRegistrar} style={{marginLeft:'auto',background:`${cor}22`,border:`1px solid ${cor}66`,borderRadius:6,padding:'4px 10px',color:cor,cursor:'pointer',fontSize:10,fontWeight:700,letterSpacing:0.3,whiteSpace:'nowrap'}}>
            {labelBtn || '+ Registrar'}
          </button>
        )}
      </div>
      <div style={{...s.cardTotal,color:cor}}>{total}</div>
      <div style={s.cardFooter}>
        <span style={s.cardSub}><span style={{color:COR.subTexto}}>↔ URA</span> <span style={{color:'#94a3b8',fontWeight:600}}>{ura}</span></span>
        <span style={s.cardSub}><span style={{color:COR.subTexto}}>⚡ Discador</span> <span style={{color:cor,fontWeight:600}}>{discador}</span></span>
      </div>
    </div>
  );
}

function TabelaDetalhe({ titulo, cor, colunas, dados }) {
  return (
    <div style={{...s.tabelaBox,borderColor:cor+'33'}}>
      <p style={{...s.tabelaTitulo,color:cor}}>{titulo} <span style={s.tabelaCount}>({dados.length})</span></p>
      <div style={s.tabelaScroll}>
        <table style={s.table}>
          <thead><tr>{colunas.map(c => <th key={c} style={s.th}>{c}</th>)}</tr></thead>
          <tbody>
            {dados.length === 0 && <tr><td colSpan={colunas.length} style={s.tdVazio}>Nenhum registro hoje</td></tr>}
            {dados.map((row,i) => (
              <tr key={i} style={i%2===0?s.trPar:s.trImpar}>
                {row.map((cell,j) => <td key={j} style={s.td}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FunnelVisual({ titulo, icone, coresEtapas, etapas }) {
  const larguras = [100, 80, 62, 46];
  return (
    <div style={{width:'100%'}}>
      <p style={{fontSize:12,fontWeight:700,textAlign:'center',margin:'0 0 14px 0',letterSpacing:1.5,textTransform:'uppercase',color:coresEtapas[0]}}>
        {icone} {titulo}
      </p>
      <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
        {etapas.map((etapa,i) => {
          const convAtual = etapa.base > 0 ? Math.round((etapa.valor/etapa.base)*100) : null;
          const acimaMeta = etapa.meta !== null && convAtual !== null ? convAtual >= etapa.meta : null;
          const progresso = etapa.meta ? Math.min(((convAtual??0)/etapa.meta)*100,100) : 0;
          const cor = coresEtapas[i];
          return (
            <div key={i} style={{width:'100%',display:'flex',flexDirection:'column',alignItems:'center'}}>
              {i > 0 && (
                <div style={{width:`${larguras[i]}%`,margin:'5px 0',padding:'5px 10px',background:'#080f1a',borderRadius:6,border:`1px solid ${acimaMeta?COR.verde+'44':'#ef444433'}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <div style={{display:'flex',alignItems:'center',gap:5}}>
                      <div style={{width:7,height:7,borderRadius:'50%',background:acimaMeta?COR.verde:'#ef4444',boxShadow:`0 0 8px ${acimaMeta?COR.verde:'#ef4444'}`}}/>
                      <span style={{color:acimaMeta?COR.verde:'#ef4444',fontSize:12,fontWeight:800}}>{convAtual??0}%</span>
                    </div>
                    <span style={{color:COR.subTexto,fontSize:11,fontWeight:600}}>meta {etapa.meta}%</span>
                  </div>
                  <div style={{width:'100%',height:3,background:COR.borda,borderRadius:2,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${progresso}%`,background:acimaMeta?`linear-gradient(90deg,${COR.verde},#4ade80)`:'linear-gradient(90deg,#ef4444,#f87171)',borderRadius:2,transition:'width 0.6s ease'}}/>
                  </div>
                </div>
              )}
              <div style={{width:`${larguras[i]}%`,background:`linear-gradient(135deg,${cor}ee,${cor}99)`,border:`1px solid ${cor}66`,borderRadius:i===0?'10px 10px 4px 4px':i===etapas.length-1?'4px 4px 10px 10px':'6px',padding:'11px 14px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:`0 4px 20px ${cor}28,inset 0 1px 0 rgba(255,255,255,0.15)`}}>
                <span style={{color:'rgba(255,255,255,0.9)',fontSize:11,fontWeight:600,letterSpacing:0.3}}>{etapa.label}</span>
                <span style={{color:'#fff',fontSize:20,fontWeight:800,textShadow:'0 1px 6px rgba(0,0,0,0.4)'}}>{etapa.valor}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  page:{minHeight:'100vh',background:COR.bg,padding:'20px 24px',fontFamily:'sans-serif'},
  topBar:{position:'fixed',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${COR.laranja},${COR.ciano},${COR.azul})`,zIndex:100},
  loadWrap:{minHeight:'100vh',background:COR.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'},
  loadSpinner:{width:40,height:40,border:`3px solid ${COR.borda}`,borderTop:`3px solid ${COR.laranja}`,borderRadius:'50%',animation:'spin 1s linear infinite'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,paddingTop:8},
  headerLeft:{display:'flex',alignItems:'center',gap:20},
  headerDivider:{width:1,height:40,background:COR.borda},
  logoBox:{display:'flex',alignItems:'baseline',gap:0},
  logoA:{color:COR.laranja,fontSize:24,fontWeight:900,letterSpacing:-1},
  logoResto:{color:COR.texto,fontSize:24,fontWeight:200,letterSpacing:4},
  logoBadge:{color:COR.subTexto,fontSize:9,fontWeight:600,letterSpacing:3,marginLeft:8,alignSelf:'center'},
  aovivo:{display:'flex',alignItems:'center',gap:6,color:COR.verde,fontSize:10,fontWeight:700,letterSpacing:1.5,marginBottom:3},
  dot:{width:6,height:6,background:COR.verde,borderRadius:'50%',display:'inline-block',animation:'pulse 2s infinite'},
  nome:{color:COR.texto,fontSize:22,fontWeight:700,margin:0},
  sub:{color:COR.subTexto,fontSize:11,marginTop:2},
  headerRight:{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end'},
  infoBox:{display:'flex',alignItems:'center',gap:6,background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'6px 12px'},
  infoLabel:{color:COR.subTexto,fontSize:11},
  infoVal:{color:COR.texto,fontSize:13,fontWeight:700,fontFamily:'monospace'},
  btnIcon:{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'8px 10px',color:COR.subTexto,cursor:'pointer'},
  btnGestor:{display:'flex',alignItems:'center',gap:6,background:COR.card,border:`1px solid ${COR.azul}`,borderRadius:8,padding:'8px 14px',color:COR.ciano,cursor:'pointer',fontSize:12,fontWeight:600},
  btnEquipe:{display:'flex',alignItems:'center',gap:6,background:COR.card,border:`1px solid ${COR.laranja}`,borderRadius:8,padding:'8px 14px',color:COR.laranja,cursor:'pointer',fontSize:12,fontWeight:600},
  btnLogout:{display:'flex',alignItems:'center',gap:6,background:COR.card,border:'1px solid #ef444455',borderRadius:8,padding:'8px 14px',color:'#ef4444',cursor:'pointer',fontSize:12},
  filtros:{display:'flex',gap:6,marginBottom:20},
  filtro:{background:COR.card,color:COR.subTexto,border:`1px solid ${COR.borda}`,borderRadius:8,padding:'7px 20px',cursor:'pointer',fontSize:13,fontWeight:500},
  filtroAtivo:{background:`linear-gradient(135deg,${COR.laranja},${COR.laranjaEsc})`,color:'#fff',border:`1px solid ${COR.laranja}`},
  mainLayout:{display:'flex',gap:20,alignItems:'flex-start'},
  leftCol:{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:14},
  cardsRow:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14},
  cardHeader:{display:'flex',alignItems:'center',gap:8,marginBottom:6},
  cardTitulo:{fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:0.5},
  cardTotal:{fontSize:44,fontWeight:800,lineHeight:1,marginBottom:10},
  cardFooter:{display:'flex',justifyContent:'space-between'},
  cardSub:{fontSize:12,display:'flex',gap:5,alignItems:'center'},
  tabelasRow:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12},
  tabelaBox:{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,padding:'12px 14px'},
  tabelaTitulo:{fontSize:11,fontWeight:700,margin:'0 0 8px 0',textTransform:'uppercase',letterSpacing:0.5},
  tabelaCount:{color:COR.subTexto,fontWeight:400},
  tabelaScroll:{maxHeight:180,overflowY:'auto'},
  table:{width:'100%',borderCollapse:'collapse',fontSize:11},
  th:{color:COR.subTexto,textAlign:'left',padding:'4px 6px',borderBottom:`1px solid ${COR.borda}`,fontWeight:600,whiteSpace:'nowrap'},
  td:{color:'#94a3b8',padding:'5px 6px',maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  tdVazio:{color:COR.subTexto2,textAlign:'center',padding:'16px 0',fontStyle:'italic'},
  trPar:{background:'transparent'},
  trImpar:{background:COR.bg},
  graficosRow:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14},
  grafico:{background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,padding:'16px'},
  graficoTitulo:{color:'#94a3b8',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6,margin:'0 0 12px 0'},
  funnelCol:{width:380,background:COR.card,border:`1px solid ${COR.borda}`,borderRadius:12,padding:'20px 18px',flexShrink:0},
  funnelHeader:{borderBottom:`1px solid ${COR.borda}`,paddingBottom:12,marginBottom:16},
  funnelTitulo:{color:COR.texto,fontSize:14,fontWeight:700,margin:0,textAlign:'center'},
  funnelSub:{color:COR.subTexto,fontSize:11,margin:'3px 0 0 0',textAlign:'center'},
  funnelDivider:{height:1,background:COR.borda,margin:'18px 0'},
};
